import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';

import { 
  getDepositCapabilites, 
  getPaymentDepositLobby } from '../../../adapters/payments';

import { usePaymentsContext } from '../../../context/paymentProvider';
import { AuthContext } from '../../../context/authProvider';
import { 
  usePaymentDrawerContext 
} from '../../../context/paymentDrawerProvider';
import { useTranslation } from '../../../context/translationProvider';
import { 
  useCreateBankAccountContext
} from '../../../context/createBankAccountProvider';

import useDeviceDetect from '../../../hooks/useDeviceDetect';
import { 
  useDepositApiData
} from '../../../hooks/graphqlStaticQuery/useDepositApiData';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { 
  useDepositPrerequisites 
} from '../../../hooks/useDepositPrerequisites';
import { useUserStatuses } from '../../../hooks/useUserStatuses';

import { DepositContent } from '../../molecules/depositContent';
import { Drawer } from '../../atoms/drawer';
import { If } from '../../atoms/if';
import { DepositBlockedMessage } from '../../molecules/depositBlockedMessage';

import { PaymentTypes } from '../../../utils/constants';

import { 
  customHeaderStyles, 
  StyledDrawerWrapper, 
  StyledMessage 
} from './styles';
import { LoadingFallback } from '../../atoms/LoadingFallback';
import { useModalContext } from '../../../context/modalProvider';

const withLoading = async (setLoading, callback) => {
  setLoading(true)
  await callback()
  setLoading(false)
}

const Deposit = ({ isPage, initialData }) => {
  const { isOpen, close: onCloseDrawer } = usePaymentDrawerContext()
  const {
    updateUser,
    user: loggedUser,
  } = useContext(AuthContext)
  const { openCreateBankAccount } = useCreateBankAccountContext()
  const { getPaymentsByType } = usePaymentsContext()

  const { user, isLoading } = useUserStatuses(updateUser)

  const { isMobile } = useDeviceDetect()
  const { translate } = useTranslation()
  const depositLobbyApiData = useDepositApiData()
  const { setSessionValue } = useSessionStorage()

   const { close: closeWithdraw } = useModalContext()

  const { 
    usableBankAccounts, 
    depositsPrerequisitesReady 
  } = useDepositPrerequisites({
    isOpen,
    isPage,
    onCloseDrawer,
    openCreateBankAccount,
    setSessionValue,
    translate,
  })

  const [isDepositAllowed, setIsDepositAllowed] = useState(false)
  const [blockedDepositReason, setBlockedDepositReason] = useState('')
  const [depositLobbyData, setDepositLobbyData] = useState(null)
  const [loadingContent, setLoadingContent] = useState(true)

  const handleDepositCapabilites = async () => {
    await withLoading(setLoadingContent, async () => {
      const { data } = await getDepositCapabilites()
      setIsDepositAllowed(!data?.block)
      setBlockedDepositReason(data?.reason)
    })
  }

  const getDepositLobby = async () => {
    await withLoading(setLoadingContent, async () => {
      const { data } = await getPaymentDepositLobby()

      if (!data) return

      const methods = data?.methods?.sort((a, b) => {
        if (a.orderNumber < b.orderNumber) {
          return -1
        }
        if (a.orderNumber > b.orderNumber) {
          return 1
        }
        return 0
      })

      data.methods = methods
      setDepositLobbyData(data)
    })
  }

  const getMethods = async () => {
    await withLoading(setLoadingContent, async () => {
      await getPaymentsByType(PaymentTypes.DEPOSIT)
    })
  }

  useEffect(() => {
    if (!user || !loggedUser || (!isOpen && !isPage)) return

    closeWithdraw()
    getMethods()
    getDepositLobby()
    handleDepositCapabilites()
  }, [isOpen])

  const content = () => (
    <If
      condition={loadingContent || isLoading}
      render={() => <LoadingFallback />}
      renderElse={() => (
        <>
          {!isDepositAllowed && (
            <DepositBlockedMessage
              blockedDepositReason={blockedDepositReason}
              onClose={onCloseDrawer}
            />
          )}

          {isDepositAllowed && user?.national_id && (
            <>
              {
                !!depositLobbyData?.methods && (
                  <DepositContent
                    initialData={initialData}
                    depositClose={onCloseDrawer}
                    user={user}
                    depositLobbyData={depositLobbyData}
                    depositLobbyCMSData={depositLobbyApiData}
                    isMobile={isMobile}
                    isPage={isPage}
                    usableBankAccounts={usableBankAccounts}
                  />
              )}

              {!depositLobbyData?.methods &&
                user?.user_status?.name?.toLowerCase() !== 'pending' && (
                  <StyledMessage>
                    {translate('account.paymentMethodsNotAvailable')?.replace(
                      '{0}',
                      user?.wallet?.currency?.symbol ||
                        user?.wallet?.currency.short_code
                    )}
                  </StyledMessage>
                )}
            </>
          )}
        </>
      )}
    />
  )

  if (isPage) return content()

  return (
    (loggedUser?.accept_tc) && (
      <StyledDrawerWrapper>
        <Drawer
          customWidth="377px"
          isMobile={isMobile}
          customHeaderStyles={isMobile ? customHeaderStyles : null}
          title={isMobile ? translate('payments.deposit.title') : null}
          isOpen={isOpen && depositsPrerequisitesReady}
          onClose={onCloseDrawer}
          id="deposit-modal"
        >
          {content()}
        </Drawer>
      </StyledDrawerWrapper>
    )
  )
}

Deposit.propTypes = {
  isPage: PropTypes.bool.isRequired,
  initialData: PropTypes.object.isRequired,
}

export { Deposit }
